import classNames from 'classnames'
import { FC, ReactNode, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AI_LISTENING_BRAND_PROFILE_ROUTE,
  AI_LISTENING_BRAND_ROUTE,
  AI_LISTENING_COMPETITORS_ROUTE,
} from 'src/constants/routes'
import { useAiListeningLink } from 'src/hooks/useAiListeningDateRange'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { BrandProfile } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './profileHeader.module.scss'
import { firstLetter } from '../utils'

export const BuildingIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
  >
    <path
      d="M2 14h12M3.5 14V6l3.5-2v10M7 14V2.5l5.5 2.5v9M9 7h1.5M9 9h1.5M9 11h1.5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.1"
    />
  </svg>
)

const GlobeIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.1" />
    <path
      d="M2 8h12M8 2c1.7 1.8 2.5 3.8 2.5 6S9.7 12.2 8 14C6.3 12.2 5.5 10.2 5.5 8S6.3 3.8 8 2z"
      stroke="currentColor"
      strokeWidth="1.1"
    />
  </svg>
)

const ExternalIcon: FC = () => (
  <svg fill="none" height="14" viewBox="0 0 14 14" width="14">
    <path
      d="M6 2.5H3a1 1 0 0 0-1 1V11a1 1 0 0 0 1 1h7.5a1 1 0 0 0 1-1V8M8.5 2H12v3.5M12 2L6.5 7.5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.1"
    />
  </svg>
)

interface ProfileTabsProps {
  active: 'brand' | 'competitors'
  profileId?: string
}

// "Your Brand's Profile | Competitors"
export const ProfileTabs: FC<ProfileTabsProps> = ({ active, profileId }) => {
  const navigate = useNavigate()
  const link = useAiListeningLink()

  return (
    <div className={styles.tabs}>
      <button
        className={classNames(styles.tab, {
          [styles.tabActive]: active === 'brand',
        })}
        onClick={() =>
          navigate(
            profileId
              ? link(AI_LISTENING_BRAND_PROFILE_ROUTE, { profileId })
              : link(AI_LISTENING_BRAND_ROUTE)
          )
        }
        type="button"
      >
        Your Brand&apos;s Profile
      </button>
      <button
        className={classNames(styles.tab, {
          [styles.tabActive]: active === 'competitors',
        })}
        disabled={!profileId}
        onClick={() =>
          profileId &&
          navigate(link(AI_LISTENING_COMPETITORS_ROUTE, { profileId }))
        }
        title={profileId ? undefined : 'Add your brand first'}
        type="button"
      >
        Competitors
      </button>
    </div>
  )
}

export interface ProfileSwitcherOption {
  id: string
  label: string
}

interface ProfileHeaderProps {
  onRemove?: () => void
  profile: BrandProfile
  right?: ReactNode
  // Other brand profiles of the user; a switcher shows when there is more
  // than one, or when it can add a new one.
  switcher?: {
    activeId: string
    onAdd?: () => void
    onSelect: (id: string) => void
    options: ProfileSwitcherOption[]
  }
}

// Avatar letter, profile name, brand and website, and the remove link.
export const ProfileHeader: FC<ProfileHeaderProps> = ({
  onRemove,
  profile,
  right,
  switcher,
}) => {
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false)
  const switcherRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    onOutsideClick: () => setIsSwitcherOpen(false),
    ref: switcherRef,
  })

  const hasSwitcher =
    !!switcher && (switcher.options.length > 1 || !!switcher.onAdd)

  return (
    <div className={styles.header}>
      <div className={styles.identity}>
        <span className={styles.avatar}>{firstLetter(profile.brandName)}</span>
        <div className={styles.text}>
          <div className={styles.nameRow} ref={switcherRef}>
            <h1 className={styles.name}>{profile.name}</h1>
            {hasSwitcher && (
              <button
                aria-label="Switch brand profile"
                className={classNames(styles.switcherButton, {
                  [styles.switcherButtonOpen]: isSwitcherOpen,
                })}
                onClick={() => setIsSwitcherOpen((open) => !open)}
                type="button"
              >
                <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.8"
                  />
                </svg>
              </button>
            )}
            {hasSwitcher && isSwitcherOpen && (
              <div className={styles.switcherMenu}>
                {switcher.options.map((option) => (
                  <button
                    className={classNames(styles.switcherItem, {
                      [styles.switcherItemActive]:
                        option.id === switcher.activeId,
                    })}
                    key={option.id}
                    onClick={() => {
                      setIsSwitcherOpen(false)
                      switcher.onSelect(option.id)
                    }}
                    type="button"
                  >
                    <span className={styles.switcherAvatar}>
                      {firstLetter(option.label)}
                    </span>
                    {option.label}
                  </button>
                ))}
                {switcher.onAdd && (
                  <button
                    className={classNames(
                      styles.switcherItem,
                      styles.switcherAdd
                    )}
                    onClick={() => {
                      setIsSwitcherOpen(false)
                      switcher.onAdd?.()
                    }}
                    type="button"
                  >
                    <span className={styles.switcherAvatar}>+</span>
                    Add brand profile
                  </button>
                )}
              </div>
            )}
          </div>
          <div className={styles.meta}>
            <span className={styles.metaItem}>
              <BuildingIcon className={styles.metaIcon} />
              <span className={styles.metaLabel}>Brand:</span>
              <span className={styles.metaValue}>{profile.brandName}</span>
            </span>
            {profile.website && (
              <span className={styles.metaItem}>
                <span className={styles.metaIcon}>
                  <GlobeIcon />
                </span>
                <span className={styles.metaLabel}>Website:</span>
                <a
                  className={styles.website}
                  href={profile.website}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {profile.website}
                  <ExternalIcon />
                </a>
              </span>
            )}
            {onRemove && (
              <>
                <span className={styles.separator} />
                <button
                  className={styles.remove}
                  onClick={onRemove}
                  type="button"
                >
                  × remove
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {right}
    </div>
  )
}
